/**
 * What happens when the tool runs, as two of the editor's sections: Action (the choice, with
 * the request or the code under it) and Response. The editor gives each its title.
 *
 * EVERY KNOB THE OLD DIALOG HAD IS STILL HERE, and Illia asked for exactly that (2026-09-26):
 * query parameters, headers, the timeout, retries, a message per kind of failure and the
 * `as` name on a mapped field. Since the request fills a section of the tool dialog (2026-09-29), they
 * are laid out open rather than folded under "More request options".
 *
 * "FIELDS THE AI CAN USE" IS THE MAPPING, AS CHIPS. The prototype's chips assumed
 * the editor already knew every field the other system answers with; ours does not until
 * somebody runs a test. So a mapped field is a chip reading `$.path → name`, pressing one
 * edits it, and after a test run the answer's top-level keys are offered as dashed chips to
 * add with one press.
 */

import { Suspense, lazy, useState } from "react";
import type { ReactNode } from "react";
import type { Connection, ErrorWhen, HttpMethod, NameValue, ToolInput } from "../../../api";
import { Close, Plus } from "../../Icons";
import { Button, IconButton, Segmented, TextArea, TextInput, Toggle, cx } from "../../../ui";
import { Picker } from "../../Listbox";
import { blankCode, blankHttp, type EditorTool } from "./toolModel";

/** CodeMirror is fetched the first time somebody picks "Run code", not with the dashboard. */
const CodeEditor = lazy(() => import("./CodeEditor"));

const METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];

export const WHENS: Array<{ when: ErrorWhen; label: string }> = [
  { when: "timeout", label: "It did not answer in time" },
  { when: "http_5xx", label: "It had a problem (5xx)" },
  { when: "http_4xx", label: "It refused the request (4xx)" },
  { when: "empty", label: "Nothing was found" },
  { when: "other", label: "Anything else" },
];

/** A code tool's failures, in the words of what went wrong with the code rather than a request. */
const CODE_WHENS: Array<{ when: ErrorWhen; label: string }> = [
  { when: "other", label: "The code failed" },
  { when: "timeout", label: "It took too long" },
];

/** What each action does, in one line under the switch. */
const KINDS: Array<{ value: EditorTool["kind"]; label: string; line: string }> = [
  { value: "save", label: "Save to the call", line: "Details appear on the call's record and in its summary email. Nothing is sent to other systems." },
  { value: "http", label: "API request", line: "Send the details to another system and use its answer." },
  { value: "code", label: "Run code", line: "Work something out, or check your systems in more than one step." },
];

/**
 * The Action section: a small switch for what the tool does, and that action's setup right
 * under it - the request, or the code. The switch changes what is below it, and nothing else
 * on the page is about it, which is why the two share a section (Illia, 2026-09-29). Picking
 * another action keeps the others' settings, so flicking back loses nothing.
 */
export function ActionChoice({
  tool,
  connections,
  errorLine,
  onChange,
}: {
  tool: EditorTool;
  connections: Connection[];
  /** The line of the code the last test run's error pointed at. */
  errorLine: number | null;
  onChange: (patch: Partial<EditorTool>) => void;
}) {
  const pick = (kind: EditorTool["kind"]) => {
    if (kind === "http") onChange({ kind, http: tool.http ?? blankHttp(connections) });
    else if (kind === "code") onChange({ kind, code: tool.code ?? blankCode(tool.params, connections) });
    else onChange({ kind });
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
      <div className="grid gap-2">
        <div className="w-max max-w-full">
          <Segmented label="Action" value={tool.kind} options={KINDS} onChange={pick} />
        </div>
        <p className="text-sm text-ink">{KINDS.find((k) => k.value === tool.kind)?.line}</p>
      </div>
      {tool.kind === "http" && <RequestEditor tool={tool} connections={connections} onChange={onChange} />}
      {tool.kind === "code" && <CodeSection tool={tool} connections={connections} errorLine={errorLine} onChange={onChange} />}
    </div>
  );
}

/** Where an API request goes and what it carries. Every option is open: this pane has the room. */
function RequestEditor({
  tool,
  connections,
  onChange,
}: {
  tool: EditorTool;
  connections: Connection[];
  onChange: (patch: Partial<EditorTool>) => void;
}) {
  const http = tool.http ?? blankHttp(connections);
  const setHttp = (patch: Partial<NonNullable<ToolInput["http"]>>) => onChange({ http: { ...http, ...patch } });
  const connection = connections.find((c) => c.id === http.connectionId) ?? null;

  return (
    <div className="grid gap-6">
      <div className="grid gap-2">
        <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2 max-[720px]:grid-cols-1">
          <Picker
            label="Method"
            className="font-mono"
            value={http.method}
            options={METHODS.map((m) => ({ value: m, label: m }))}
            onChange={(method) => setHttp({ method, body: method === "GET" ? "" : http.body })}
          />
          <div className="field-shell flex min-w-0 items-center rounded-sm border border-line bg-paper">
            {connection && (
              <span className="max-w-60 flex-none truncate border-r border-line-soft bg-sunken px-2.5 py-[8px] font-mono text-sm text-ink" title={connection.baseUrl}>
                {connection.baseUrl}
              </span>
            )}
            <input
              aria-label={connection ? "Path" : "Address"}
              className="min-w-0 flex-1 bg-transparent px-2.5 py-[8px] font-mono text-sm outline-none placeholder:text-ink-ghost"
              placeholder={connection ? "/orders/{{order_number}}.json" : "https://api.example.com/orders/{{order_number}}"}
              value={http.path}
              onChange={(event) => setHttp({ path: event.target.value })}
            />
          </div>
        </div>
        <p className="text-sm text-ink">
          Put <code className="font-mono">{"{{parameter}}"}</code> anywhere to fill in what the caller said.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 text-sm text-ink">
        Connection
        <Picker
          label="Connection"
          rootClassName="w-72"
          value={http.connectionId ?? ""}
          options={[{ value: "", label: "None: a full address above" }, ...connections.map((c) => ({ value: c.id, label: c.name, note: c.baseUrl }))]}
          onChange={(id) => setHttp({ connectionId: id || null })}
        />
      </div>

      <RequestOptions http={http} onChange={setHttp} />
    </div>
  );
}

/** What comes back from an API request: the fields the AI may use, and what it hears when there is nothing. */
export function ResponseEditor({
  tool,
  suggestions,
  onChange,
}: {
  tool: EditorTool;
  /** Top-level fields of the last test run's answer, offered as chips to map. */
  suggestions: string[];
  onChange: (patch: Partial<EditorTool>) => void;
}) {
  return (
    <div className="grid gap-7">
      <Mapping tool={tool} suggestions={suggestions} onChange={onChange} />
      <div className="border-t border-line-soft pt-6">
        <ErrorMessages tool={tool} whens={WHENS} title="If the request fails, tell the AI" onChange={onChange} />
      </div>
    </div>
  );
}

/** "Run code": the code, with its own toolbar and completions, and what the AI is told when it fails. */
function CodeSection({
  tool,
  connections,
  errorLine,
  onChange,
}: {
  tool: EditorTool;
  connections: Connection[];
  errorLine: number | null;
  onChange: (patch: Partial<EditorTool>) => void;
}) {
  return (
    // minmax(0, 1fr): a grid item is otherwise as wide as its widest content, and the editor's
    // is its longest line - which pushed the whole section past the form column.
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
      <Suspense fallback={<div className="h-[290px] rounded-lg border border-line bg-paper" aria-busy="true" />}>
        <CodeEditor
          value={tool.code ?? ""}
          onChange={(code) => onChange({ code })}
          errorLine={errorLine}
          params={tool.params}
          connections={connections.map((c) => c.name)}
        />
      </Suspense>
      <div className="border-t border-line-soft pt-6">
        <ErrorMessages tool={tool} whens={CODE_WHENS} title="If the code fails, tell the AI" onChange={onChange} />
      </div>
    </div>
  );
}

function ErrorMessages({
  tool,
  whens,
  title,
  onChange,
}: {
  tool: EditorTool;
  whens: Array<{ when: ErrorWhen; label: string }>;
  title: string;
  onChange: (patch: Partial<EditorTool>) => void;
}) {
  return (
    <div className="grid gap-2">
      <div className="grid gap-0.5">
        <span className="text-sm font-medium text-ink">{title}</span>
        <span className="text-sm text-ink">Instead of the raw error. Leave one empty for the default.</span>
      </div>
      {whens.map(({ when, label }) => {
        const rule = tool.errors.find((e) => e.when === when);
        return (
          <div key={when} className="grid grid-cols-[220px_minmax(0,1fr)] items-center gap-3 max-[720px]:grid-cols-1">
            <span className="text-sm text-ink">{label}</span>
            <TextInput
              aria-label={label}
              className="w-full"
              value={rule?.message ?? ""}
              placeholder="Say you can't check right now and offer to take a message."
              onChange={(event) => {
                const others = tool.errors.filter((e) => e.when !== when);
                const message = event.target.value;
                onChange({ errors: message ? [...others, { when, message }] : others });
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

function Mapping({
  tool,
  suggestions,
  onChange,
}: {
  tool: EditorTool;
  suggestions: string[];
  onChange: (patch: Partial<EditorTool>) => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const mapping = tool.mapping;
  const offered = suggestions.filter((key) => !mapping.some((m) => m.as === key || m.path === `$.${key}`));
  const set = (index: number, patch: Partial<{ path: string; as: string }>) =>
    onChange({ mapping: mapping.map((m, i) => (i === index ? { ...m, ...patch } : m)) });

  return (
    <div className="grid gap-2">
      <div className="grid gap-0.5">
        <span className="text-sm font-medium text-ink">Fields the AI can use</span>
        <span className="text-sm text-ink">
          The other system replies with these fields. The AI only sees the ones listed here, and can mention them to the caller.
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {mapping.map((m, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setEditing(editing === i ? null : i)}
            className={cx(
              "h-7.5 rounded-full border px-3 font-mono text-sm",
              editing === i ? "border-accent bg-accent-soft text-ink" : "border-accent-line bg-accent-soft/60 text-ink",
            )}
          >
            {m.path || "$."} → {m.as || "?"}
          </button>
        ))}
        {offered.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange({ mapping: [...mapping, { path: `$.${key}`, as: key }] })}
            className="h-7.5 rounded-full border border-dashed border-line-strong px-3 font-mono text-sm text-ink hover:border-accent hover:text-accent"
          >
            + {key}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            onChange({ mapping: [...mapping, { path: "$.", as: "" }] });
            setEditing(mapping.length);
          }}
          className="flex h-7.5 items-center gap-1 rounded-full px-2 text-sm font-medium text-accent hover:bg-accent-soft"
        >
          <Plus size={13} />
          Add field
        </button>
      </div>
      {editing !== null && mapping[editing] && (
        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] items-center gap-2">
          <TextInput aria-label="From (JSONPath)" className="font-mono" placeholder="$.orders[0].status" value={mapping[editing]!.path} onChange={(event) => set(editing, { path: event.target.value })} />
          <TextInput aria-label="As" className="font-mono" placeholder="status" value={mapping[editing]!.as} onChange={(event) => set(editing, { as: event.target.value })} />
          <IconButton
            type="button"
            title="Remove field"
            onClick={() => {
              onChange({ mapping: mapping.filter((_, i) => i !== editing) });
              setEditing(null);
            }}
          >
            <Close size={14} />
            <span className="sr-only">Remove field</span>
          </IconButton>
        </div>
      )}
      {mapping.length === 0 ? (
        <p className="text-sm text-ink">None listed, so the AI sees the whole reply.</p>
      ) : (
        <Toggle checked={tool.dropUnmapped} onChange={(dropUnmapped) => onChange({ dropUnmapped })}>
          Hide every other field from the AI
        </Toggle>
      )}
    </div>
  );
}

/** Query, headers, body, timeout and retries. */
function RequestOptions({
  http,
  onChange,
}: {
  http: NonNullable<ToolInput["http"]>;
  onChange: (patch: Partial<NonNullable<ToolInput["http"]>>) => void;
}) {
  return (
    <div className="grid gap-6 border-t border-line-soft pt-6">
      <Labelled label="Query parameters">
        <Pairs rows={http.query} left="Name" right="Value" onChange={(query) => onChange({ query })} />
      </Labelled>
      <Labelled label="Headers" hint="Credentials come from the connection, not from here.">
        <Pairs rows={http.headers} left="Name" right="Value" onChange={(headers) => onChange({ headers })} />
      </Labelled>
      {http.method !== "GET" && (
        <Labelled label="Request body (JSON)" hint={'A string that is exactly "{{parameter}}" becomes the value itself, number or list included.'}>
          <TextArea
            aria-label="Request body"
            className="w-full font-mono text-sm"
            rows={6}
            value={http.body}
            placeholder={'{\n  "order": "{{order_number}}"\n}'}
            onChange={(event) => onChange({ body: event.target.value })}
          />
        </Labelled>
      )}
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-ink">
          Timeout
          <TextInput type="number" min={1} max={15} className="w-20 font-mono" value={http.timeoutMs / 1000} onChange={(event) => onChange({ timeoutMs: Math.round(Number(event.target.value) * 1000) })} />
          sec
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          Retries
          <TextInput type="number" min={0} max={2} className="w-20 font-mono" value={http.retries} onChange={(event) => onChange({ retries: Number(event.target.value) })} />
        </label>
      </div>
    </div>
  );
}

function Labelled({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="text-sm text-ink">{hint}</span>}
    </div>
  );
}

/** Rows of two text inputs: query parameters and headers. */
function Pairs({
  rows,
  left,
  right,
  onChange,
}: {
  rows: NameValue[];
  left: string;
  right: string;
  onChange: (next: NameValue[]) => void;
}) {
  const set = (index: number, patch: Partial<NameValue>) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <div className="grid gap-2">
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] items-center gap-2">
          <TextInput aria-label={left} className="w-full font-mono" placeholder={left} value={row.name} onChange={(event) => set(index, { name: event.target.value })} />
          <TextInput aria-label={right} className="w-full font-mono" placeholder={right} value={row.value} onChange={(event) => set(index, { value: event.target.value })} />
          <IconButton type="button" title="Remove" onClick={() => onChange(rows.filter((_, i) => i !== index))}>
            <Close size={14} />
            <span className="sr-only">Remove</span>
          </IconButton>
        </div>
      ))}
      <div>
        <Button type="button" variant="ghost" onClick={() => onChange([...rows, { name: "", value: "" }])}>
          <Plus size={15} />
          Add
        </Button>
      </div>
    </div>
  );
}
